import { debugDir, debugFn } from '@socketsecurity/registry/lib/debug'
import { envAsString } from '@socketsecurity/registry/lib/env'
import { logger } from '@socketsecurity/registry/lib/logger'

import { getDefaultOrgSlug } from './fetch-default-org-slug.mts'
import constants from '../../constants.mts'
import {
  detectDefaultBranch,
  getRepoName,
  gitBranch,
} from '../../utils/git.mts'
import { serializeResultJson } from '../../utils/serialize-result-json.mts'
import { handleCreateNewScan } from '../scan/handle-create-new-scan.mts'

/**
 * Derive the pull request number from the CI environment. GitHub Actions
 * pull_request events check out `refs/pull/<n>/merge`, so the number is
 * recoverable from GITHUB_REF; returns 0 outside a PR run (the API omits
 * `pull_request` for falsy values).
 */
export function detectCiPullRequestNumber(): number {
  const match = /^refs\/pull\/(\d+)\//.exec(
    envAsString(process.env['GITHUB_REF']),
  )
  return match ? Number(match[1]) : 0
}

export async function handleCi(autoManifest: boolean): Promise<void> {
  debugFn('notice', 'Starting CI scan')
  debugDir('inspect', { autoManifest })

  const orgSlugCResult = await getDefaultOrgSlug()
  if (!orgSlugCResult.ok) {
    debugFn('warn', 'Failed to get default org slug')
    debugDir('inspect', { orgSlugCResult })
    process.exitCode = orgSlugCResult.code ?? 1
    // Always assume json mode.
    logger.log(serializeResultJson(orgSlugCResult))
    return
  }

  const orgSlug = orgSlugCResult.data
  const cwd = process.cwd()
  const branchName = (await gitBranch(cwd)) || (await detectDefaultBranch(cwd))
  const repoName = await getRepoName(cwd)

  debugFn(
    'notice',
    `CI scan for ${orgSlug}/${repoName} on branch ${branchName}`,
  )
  debugDir('inspect', { orgSlug, cwd, branchName, repoName })

  await handleCreateNewScan({
    autoManifest,
    branchName,
    commitMessage: '',
    commitHash: '',
    committers: '',
    cwd,
    defaultBranch: false,
    interactive: false,
    orgSlug,
    outputKind: 'json',
    // When 'pendingHead' is true, it requires 'branchName' set and 'tmp' false.
    pendingHead: true,
    pullRequest: detectCiPullRequestNumber(),
    reach: {
      dynamicSbomInference: false,
      excludePaths: [],
      reachAdvisories: [],
      reachAnalysisMemoryLimit: '',
      reachAnalysisTimeout: '',
      reachConcurrency: 1,
      reachContinueOnAnalysisErrors: false,
      reachContinueOnInstallErrors: false,
      reachContinueOnMissingLockFiles: false,
      reachContinueOnNoSourceFiles: false,
      reachDebug: false,
      reachDetailedAnalysisLogFile: false,
      reachDisableAnalytics: false,
      reachDisableDependencyDownloads: false,
      reachDisableExternalToolChecks: false,
      reachEcosystems: [],
      reachEnableAnalysisSplitting: false,
      reachExcludePaths: [],
      reachFallbackToRegularScan: false,
      reachRetainFactsFile: false,
      reachSkipCache: false,
      reachUseOnlyPregeneratedSboms: false,
      reachVersion: undefined,
      runReachabilityAnalysis: false,
    },
    repoName,
    readOnly: false,
    report: true,
    reportLevel: constants.REPORT_LEVEL_ERROR,
    targets: ['.'],
    // Don't set 'tmp' when 'pendingHead' is true.
    tmp: false,
  })
}
